/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Stack, Text, Badge } from '@chakra-ui/react';
import { useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import { apiAdminExtractVeteransResume, AdminExtractVeteransResumeResponse } from '@/api/services';
import FileUploadCard from '@/components/forms/FileUploadCard';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;
const MAX_FILE_SIZE_MB = MAX_RESUME_FILE_SIZE_MB;

export default function VeteransResumeExtractorSection() {
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const {
    resumeFile,
    setResumeFile,
    clearResumeFile,
    veteransResume,
    setVeteransResume,
    clearVeteransResume,
    clearResumeAnalysisResult
  } = useAdminAnalysis();

  const handleFileUpload = async (file: File) => {
    setResumeFile(file);
    setFileUploading(true);
    clearVeteransResume();
    clearResumeAnalysisResult();
    setFileError(null);

    if (file.size > FILE_SIZE_LIMIT) {
      setFileError(`File size exceeds ${MAX_FILE_SIZE_MB} MB`);
      setFileUploading(false);
      return;
    }

    try {
      const response = await fetchAPI<AdminExtractVeteransResumeResponse>({
        request: () => apiAdminExtractVeteransResume({ payload: { file } })
      });

      if (response.success) {
        setVeteransResume(response.data);
      } else {
        handleFileError('Unable to extract resume', response.message);
      }
    } catch (error) {
      handleFileError('Unable to extract resume', error);
    } finally {
      setFileUploading(false);
    }
  };

  const handleFileError = (message: string, error: any) => {
    console.error(message, error);
    setFileError(message);
  };

  const resetFileState = () => {
    clearResumeFile();
    clearVeteransResume();
    clearResumeAnalysisResult();
    setFileError(null);
  };

  return (
    <Stack gap="24px" width="100%">
      <Stack gap="16px" align="flex-start" width="100%">
        <FileUploadCard
          file={resumeFile}
          uploading={fileUploading}
          error={fileError}
          maxSizeMB={MAX_FILE_SIZE_MB}
          onChange={handleFileUpload}
          onReset={resetFileState}
        />
      </Stack>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Extracted Resume
      </Text>
      {veteransResume ? (
        <Stack gap="16px">
          {veteransResume.military_types.length > 0 && (
            <Stack gap="8px">
              <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                Military Types
              </Text>
              <Box display="flex" flexWrap="wrap" gap="8px">
                {veteransResume.military_types.map((type, index) => (
                  <Badge
                    key={index}
                    colorPalette="blue"
                    variant="subtle"
                    fontSize="13px"
                    px="10px"
                    py="4px"
                    borderRadius="6px"
                  >
                    {type}
                  </Badge>
                ))}
              </Box>
            </Stack>
          )}
          <Stack gap="8px">
            <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
              Content
            </Text>
            <Box
              p="16px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutrals.200"
              bg="neutrals.50"
              fontSize="14px"
              color="neutrals.800"
              whiteSpace="pre-wrap"
            >
              {veteransResume.content}
            </Box>
          </Stack>
        </Stack>
      ) : (
        <Text>No resume extracted yet.</Text>
      )}
    </Stack>
  );
}
